import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Search, UserCheck, X } from "lucide-react";

export default function CaseloadTab({
  C, sharedIndividuals, allNotes, teamMembers, user, profile,
}) {
  const navigate = useNavigate();
  const [caseloadSearch, setCaseloadSearch] = useState("");
  const [caseloadAgentFilter, setCaseloadAgentFilter] = useState("all");
  const [deleteIndividualConfirm, setDeleteIndividualConfirm] = useState(null);
  const [deactivatePin, setDeactivatePin] = useState("");
  const [deactivatePinError, setDeactivatePinError] = useState("");

  const agents = teamMembers.filter(m => m.role === "agent" || m.role === "counselor");

  const filteredIndividuals = useMemo(() => {
    let filtered = sharedIndividuals;
    if (caseloadAgentFilter === "unassigned") {
      filtered = filtered.filter(i => !(i.assignedAgents || []).length);
    } else if (caseloadAgentFilter !== "all") {
      filtered = filtered.filter(i => (i.assignedAgents || []).includes(caseloadAgentFilter));
    }
    if (caseloadSearch) {
      const q = caseloadSearch.toLowerCase();
      filtered = filtered.filter(i =>
        `${i.firstName} ${i.lastName}`.toLowerCase().includes(q) ||
        (i.diagnoses || []).some(d => d.toLowerCase().includes(q)) ||
        (i.primaryAgentName || "").toLowerCase().includes(q)
      );
    }
    return filtered.sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
  }, [sharedIndividuals, caseloadAgentFilter, caseloadSearch]);

  const riskCounts = {
    high: sharedIndividuals.filter(i => i.riskLevel === "high").length,
    moderate: sharedIndividuals.filter(i => i.riskLevel === "moderate").length,
    low: sharedIndividuals.filter(i => i.riskLevel === "low" || !i.riskLevel).length,
  };

  return (
    <div>
      {/* Header with Add + Search */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button onClick={() => navigate("/onboarding")} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", borderRadius: 12,
          fontSize: 12, fontWeight: 800, background: "linear-gradient(135deg, #3182ce, #2b6cb0)",
          color: "white", border: "none", cursor: "pointer", whiteSpace: "nowrap",
        }}>
          <UserPlus size={14} /> Add Individual
        </button>
        <div style={{ flex: 1, position: "relative" }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#4a5568" }} />
          <input placeholder="Search individuals..." value={caseloadSearch} onChange={e => setCaseloadSearch(e.target.value)}
            style={{ width: "100%", padding: "10px 12px 10px 34px", borderRadius: 12, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 12, outline: "none" }} />
        </div>
      </div>

      {/* Agent Filter */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {[{ key: "all", label: `All (${sharedIndividuals.length})` }, { key: "unassigned", label: "Unassigned" },
          ...agents.map(a => ({ key: a.id, label: a.displayName || a.email }))
        ].map(f => (
          <button key={f.key} onClick={() => setCaseloadAgentFilter(f.key)} style={{
            padding: "5px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap",
            background: caseloadAgentFilter === f.key ? "rgba(99,179,237,0.15)" : "rgba(15,23,42,0.5)",
            color: caseloadAgentFilter === f.key ? "#63b3ed" : "#718096",
            border: `1px solid ${caseloadAgentFilter === f.key ? "rgba(99,179,237,0.2)" : "rgba(99,179,237,0.06)"}`,
            cursor: "pointer",
          }}>{f.label}</button>
        ))}
      </div>

      {/* Risk stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
        {[
          { label: "High Risk", value: riskCounts.high, color: "#fc8181" },
          { label: "Moderate", value: riskCounts.moderate, color: "#ecc94b" },
          { label: "Low Risk", value: riskCounts.low, color: "#68d391" },
        ].map((s, i) => (
          <div key={i} style={{ ...C.card, textAlign: "center", padding: 12 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
            <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {filteredIndividuals.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <UserCheck size={32} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, color: "#4a5568" }}>No individuals found</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
          {filteredIndividuals.map(ind => {
            const indNotes = allNotes.filter(n =>
              n.individualId === ind.id ||
              (n.individualName || "").toLowerCase().includes((ind.lastName || "").toLowerCase())
            );
            const riskColor = ind.riskLevel === "high" ? "#fc8181" : ind.riskLevel === "moderate" ? "#ecc94b" : "#68d391";
            return (
              <div key={ind.id} style={{ ...C.card, cursor: "pointer", position: "relative" }}
                onClick={() => navigate(`/individual/${ind.id}`)}>
                <button onClick={e => { e.stopPropagation(); setDeleteIndividualConfirm(ind); }}
                  style={{ position: "absolute", top: 8, right: 8, width: 24, height: 24, borderRadius: 6, background: "rgba(252,129,129,0.08)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={12} style={{ color: "#fc8181" }} />
                </button>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 6, paddingRight: 24 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{ind.lastName}, {ind.firstName}</div>
                    <div style={{ fontSize: 10, color: "#718096", marginTop: 1 }}>{ind.dob} · {ind.program || "ICM"}</div>
                  </div>
                  <span style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", padding: "2px 7px", borderRadius: 20, background: `${riskColor}15`, color: riskColor }}>{ind.riskLevel}</span>
                </div>
                <div style={{ fontSize: 10, color: "#4a5568", marginBottom: 6 }}>
                  Agent: {ind.primaryAgentName || (ind.assignedAgentNames || []).join(", ") || "Unassigned"}
                </div>
                {(ind.diagnoses || []).length > 0 && (
                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 6 }}>
                    {ind.diagnoses.slice(0, 2).map((dx, j) => (
                      <span key={j} style={{ fontSize: 8, fontWeight: 600, padding: "2px 6px", borderRadius: 8, background: "rgba(183,148,244,0.08)", color: "#b794f4" }}>{dx}</span>
                    ))}
                    {ind.diagnoses.length > 2 && <span style={{ fontSize: 8, color: "#4a5568" }}>+{ind.diagnoses.length - 2}</span>}
                  </div>
                )}
                <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                  <span style={{ fontSize: 10, color: "#4fd1c5" }}>{indNotes.length} notes</span>
                  {ind.lastContactDate && (
                    <span style={{ fontSize: 10, color: "#718096" }}>
                      Last: {new Date(ind.lastContactDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Deactivate modal */}
      {deleteIndividualConfirm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)", padding: 20 }}
          onClick={() => { setDeleteIndividualConfirm(null); setDeactivatePin(""); setDeactivatePinError(""); }}>
          <div style={{ background: "linear-gradient(135deg, #0f1729, #162033)", border: "1px solid rgba(252,129,129,0.15)", borderRadius: 16, padding: 24, maxWidth: 400, width: "100%" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#f6ad55", marginBottom: 8 }}>Deactivate Individual</div>
            <div style={{ fontSize: 13, color: "#a0aec0", lineHeight: 1.6, marginBottom: 12 }}>
              Remove <strong style={{ color: "#e2e8f0" }}>{deleteIndividualConfirm.firstName} {deleteIndividualConfirm.lastName}</strong> from the active caseload? The record will be archived — never permanently deleted.
            </div>
            <div style={{ fontSize: 11, color: "#718096", marginBottom: 16 }}>Enter admin PIN to confirm.</div>
            <input type="password" inputMode="numeric" maxLength={6} placeholder="Enter PIN" value={deactivatePin}
              onChange={e => { setDeactivatePin(e.target.value.replace(/\D/g, "")); setDeactivatePinError(""); }}
              autoFocus
              style={{ width: "100%", padding: "12px 16px", borderRadius: 12, fontSize: 18, fontWeight: 800, textAlign: "center", letterSpacing: 8, background: "rgba(15,23,42,0.5)", border: `1px solid ${deactivatePinError ? "rgba(252,129,129,0.4)" : "rgba(99,179,237,0.08)"}`, color: "#e2e8f0", outline: "none", marginBottom: 8 }}
              onKeyDown={e => { if (e.key === "Enter") document.getElementById("deactivate-btn")?.click(); }}
            />
            {deactivatePinError && <div style={{ fontSize: 11, color: "#fc8181", marginBottom: 8, textAlign: "center" }}>{deactivatePinError}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button id="deactivate-btn" onClick={async () => {
                const storedPin = localStorage.getItem("fieldnotes_admin_pin") || "1234";
                if (deactivatePin !== storedPin) { setDeactivatePinError("Incorrect PIN"); return; }
                try {
                  const { updateDoc, doc: firestoreDoc, serverTimestamp } = await import("firebase/firestore");
                  const { db: firestoreDb } = await import("../../services/firebase");
                  await updateDoc(firestoreDoc(firestoreDb, "sharedIndividuals", deleteIndividualConfirm.id), {
                    status: "inactive", deactivatedAt: serverTimestamp(),
                    deactivatedBy: profile?.displayName || user?.email,
                    deactivatedByUid: user?.uid, deactivationReason: "Admin deactivation from dashboard",
                  });
                } catch (e) { setDeactivatePinError("Failed: " + e.message); return; }
                setDeleteIndividualConfirm(null); setDeactivatePin(""); setDeactivatePinError("");
              }} style={{ flex: 1, padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 800, background: "linear-gradient(135deg, #dd6b20, #c05621)", color: "white", border: "none", cursor: "pointer" }}>Deactivate</button>
              <button onClick={() => { setDeleteIndividualConfirm(null); setDeactivatePin(""); setDeactivatePinError(""); }} style={{ flex: 1, padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 600, background: "transparent", color: "#718096", border: "1px solid rgba(99,179,237,0.08)", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}